import React, { useMemo } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { InfoOutlined as InfoIcon } from '@mui/icons-material';

import { getParsedRevision } from '../../lib/demoEdgeRevisionUtils';
import './_styles.scss';

const parseComponentName = (fullName, isSplitCamelCase) => {
  const nameString = String(fullName);
  let lastSegment = nameString.split('.').pop() || nameString;

  if (isSplitCamelCase) {
    lastSegment = lastSegment.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  if (nameString.startsWith('aws.')) {
    return { vendor: 'AWS', name: lastSegment };
  }
  if (nameString.startsWith('com.iotecha.')) {
    return { vendor: 'IoT.ON', name: lastSegment };
  }

  return { vendor: null, name: lastSegment };
};

const flattenParameters = (obj, prefix = '') => {
  const result = [];

  const flatten = (current, path) => {
    if (current === null || current === undefined) {
      result.push({ path, value: String(current) });
      return;
    }

    if (typeof current !== 'object') {
      result.push({ path, value: String(current) });
      return;
    }

    if (Array.isArray(current)) {
      if (current.length === 0) {
        result.push({ path, value: '[]' });
      } else {
        current.forEach((item, index) => {
          flatten(item, path ? `${path}.${index}` : String(index));
        });
      }
    } else {
      const keys = Object.keys(current);
      if (keys.length === 0) {
        result.push({ path, value: '{}' });
      } else {
        keys.forEach((key) => {
          flatten(current[key], path ? `${path}.${key}` : key);
        });
      }
    }
  };

  flatten(obj, prefix);
  return result;
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return String(timestamp);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const DemoEdgeComponentsList = ({ revision = null, parsedRevision: parsedRevisionProp = null }) => {
  const parsedRevision = useMemo(() => {
    if (parsedRevisionProp) return parsedRevisionProp;
    if (revision) return getParsedRevision(revision);
    return getParsedRevision();
  }, [parsedRevisionProp, revision]);

  const components = useMemo(() => {
    const list = parsedRevision?.components || [];
    const vendorPriority = (vendor) => {
      if (vendor === 'IoT.ON') return 0;
      if (vendor === 'AWS') return 1;
      return 2;
    };

    return [...list].sort((a, b) => {
      const leftParsed = parseComponentName(a?.name);
      const rightParsed = parseComponentName(b?.name);

      const vendorOrder = vendorPriority(leftParsed.vendor) - vendorPriority(rightParsed.vendor);

      if (vendorOrder !== 0) return vendorOrder;

      const nameOrder = String(leftParsed.name).localeCompare(String(rightParsed.name));

      if (nameOrder !== 0) return nameOrder;

      return String(a?.name).localeCompare(String(b?.name));
    });
  }, [parsedRevision]);

  if (!parsedRevision) {
    return (
      <div className="edge-components-empty">
        <p>No components found.</p>
      </div>
    );
  }

  return (
    <div className="edge-components-table">
      <div className="edge-components-table-header">
        <div className="header-cell">Component Name</div>
        <div className="header-cell">Component Version</div>
      </div>

      <div className="components-list edge-components-table-body">
        {components.map((component, idx) => {
          const parsedName = parseComponentName(component.name, true);
          const timestampValue = component.lastStatusChangeTimestamp || component.lastStatusChangeAt;
          const formattedTimestamp = formatTimestamp(timestampValue);
          const descriptionText = component?.description;

          return (
            <Accordion key={`${component.name}-${idx}`} className="component-accordion" square disableGutters>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`component-${idx}-content`}
                id={`component-${idx}-header`}
                className="component-header"
              >
                <div className="edge-components-row">
                  <div className="edge-components-name">
                    <div className="component-name-row">
                      {parsedName.vendor && (
                        <span className={`component-vendor-badge ${parsedName.vendor === 'AWS' ? 'aws' : 'ioton'}`}>
                          {parsedName.vendor}
                        </span>
                      )}
                      <h4 className="component-name">
                        <span>{parsedName.name}</span>
                        <Tooltip
                          title={descriptionText || ''}
                          arrow
                          placement="top"
                          disableHoverListener={!descriptionText}
                          disableFocusListener={!descriptionText}
                          disableTouchListener={!descriptionText}
                          classes={{
                            tooltip: 'edge-components-description-tooltip',
                          }}
                        >
                          <span
                            className="component-description-icon"
                            aria-label="Component description"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <InfoIcon fontSize="small" style={{ color: '#2196F3' }} />
                          </span>
                        </Tooltip>
                      </h4>
                    </div>
                  </div>
                  <div className="edge-components-version">
                    <span className="component-version">{component.version}</span>
                  </div>
                </div>
              </AccordionSummary>

              <AccordionDetails className="component-parameters" id={`component-${idx}-content`}>
                {formattedTimestamp && (
                  <div className="edge-components-meta">
                    <div className="edge-components-meta-item">
                      <span className="edge-components-meta-label">Last status change</span>
                      <span className="edge-components-meta-value">{formattedTimestamp}</span>
                    </div>
                  </div>
                )}

                <div className="parameters-content">
                  {Object.keys(component.parameters || {}).length === 0 ? (
                    <div className="edge-components-no-params">No parameters</div>
                  ) : (
                    flattenParameters(component.parameters).map((param, paramIdx) => {
                      const displayPath = param.path.length > 60 ? `...${param.path.slice(-60)}` : param.path;
                      return (
                        <div key={paramIdx} className="param-item">
                          <div className="param-path" title={param.path}>
                            {displayPath}
                          </div>
                          <div className="param-value">{param.value}</div>
                        </div>
                      );
                    })
                  )}
                </div>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </div>
    </div>
  );
};

export default DemoEdgeComponentsList;
